import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useRoutedNavigation } from "presentation/hooks/useRoutedNavigation";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import GavelIcon from '@mui/icons-material/Gavel';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BusinessIcon from '@mui/icons-material/Business';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import ListAltIcon from '@mui/icons-material/ListAlt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import VerifiedIcon from '@mui/icons-material/Verified';
import PageContainer from '../components/shared/pageContainer';
import PageHeader from '../components/shared/page-header';
import { DetailSkeleton } from '../components/shared/skeletons';
import { usePncpBiddingDetailController } from './context/pncp-bidding-detail-controller';
import { usePncpBiddingDetailState } from './context/pncp-bidding-detail-context';
import { formatDate, formatDateTime, formatCurrency, getBiddingStatusColor, openExternalLink } from 'utils';

function InfoField({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'start', mb: 2 }}>
      <Box sx={{ mr: 1, mt: 0.5, color: 'text.secondary', display: 'flex' }}>{icon}</Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {label}
        </Typography>
        {children}
      </Box>
    </Box>
  );
}

export function PncpBiddingDetailComponent() {
  const { id } = useParams<{ id: string }>();
  const { navigateTo: navigate } = useRoutedNavigation();
  const { loadDetail, loadItems } = usePncpBiddingDetailController();
  const { bidding, loading, error, items, itemsPage, hasMoreItems, loadingItems, itemsError } = usePncpBiddingDetailState();

  useEffect(() => {
    if (id) {
      loadDetail(parseInt(id));
      loadItems(parseInt(id), 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const backButton = (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 3 }}>
      <Button
        variant="contained"
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/licitacoes')}
        size="large"
        sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 4, py: 1.5 }}
      >
        Voltar para Licitações
      </Button>
    </Box>
  );

  if (loading) {
    return <DetailSkeleton hasImage={false} hasSections={3} />;
  }

  if (error) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
          {backButton}
        </Container>
      </PageContainer>
    );
  }

  if (!bidding?.purchase) {
    return (
      <PageContainer>
        <Container maxWidth="lg">
          <Alert severity="info" sx={{ mt: 2 }}>
            Licitação não encontrada.
          </Alert>
          {backButton}
        </Container>
      </PageContainer>
    );
  }

  const purchase = bidding.purchase;
  const number = purchase.numberPurchase
    ? `${purchase.numberPurchase}/${purchase.yearPurchase}`
    : `${purchase.sequence}/${purchase.yearPurchase}`;
  const documents = bidding.documents ?? [];

  return (
    <PageContainer>
      <Container maxWidth="lg">
        <PageHeader
          title={`Licitação ${number}`}
          subtitle={purchase.modalityName || 'Detalhes da licitação'}
          breadcrumbs={[
            { label: 'Licitações', href: '/licitacoes' },
            { label: number },
          ]}
        />

        {/* Informações Principais */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <GavelIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  {number}
                </Typography>
                <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Chip icon={<VerifiedIcon />} label="PNCP" color="success" />
                  {purchase.situationName && (
                    <Chip label={purchase.situationName} color={getBiddingStatusColor(purchase.situationName)} />
                  )}
                  <Chip label={purchase.modalityName || 'Modalidade não informada'} variant="outlined" color="primary" />
                  {purchase.indSrp && <Chip label="Registro de Preços" variant="outlined" />}
                </Box>
              </Box>
            </Box>
            {bidding.externalUrl && (
              <Button
                variant="outlined"
                endIcon={<OpenInNewIcon />}
                onClick={() => openExternalLink(bidding.externalUrl)}
                sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
              >
                Ver no PNCP
              </Button>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Grid container spacing={3}>
            <Grid item xs={12}>
              <InfoField icon={<DescriptionIcon />} label="Objeto da Licitação">
                <Typography variant="body1">{purchase.objectPurchase || 'Não informado'}</Typography>
              </InfoField>
            </Grid>

            <Grid item xs={12} md={6}>
              <InfoField icon={<BusinessIcon />} label="Órgão / Unidade">
                <Typography variant="body1">{purchase.orgaoEntidade?.companyName || 'Não informado'}</Typography>
                {purchase.unidadeOrgao?.name && (
                  <Typography variant="caption" color="text.secondary">
                    {purchase.unidadeOrgao.unitCode ? `${purchase.unidadeOrgao.unitCode} - ` : ''}{purchase.unidadeOrgao.name}
                  </Typography>
                )}
              </InfoField>
            </Grid>

            {purchase.processNumber && (
              <Grid item xs={12} md={6}>
                <InfoField icon={<DescriptionIcon />} label="Número do Processo">
                  <Typography variant="body1">{purchase.processNumber}</Typography>
                </InfoField>
              </Grid>
            )}

            {purchase.typeCallingInstrument && (
              <Grid item xs={12} md={6}>
                <InfoField icon={<DescriptionIcon />} label="Instrumento Convocatório">
                  <Typography variant="body1">{purchase.typeCallingInstrument}</Typography>
                </InfoField>
              </Grid>
            )}

            {purchase.disputeModeName && (
              <Grid item xs={12} md={6}>
                <InfoField icon={<GavelIcon />} label="Modo de Disputa">
                  <Typography variant="body1">{purchase.disputeModeName}</Typography>
                </InfoField>
              </Grid>
            )}

            {purchase.amountEstimated != null && (
              <Grid item xs={12} md={6}>
                <InfoField icon={<AttachMoneyIcon />} label="Valor Estimado">
                  <Typography variant="body1" fontWeight={600} color="success.main">
                    {formatCurrency(purchase.amountEstimated)}
                  </Typography>
                </InfoField>
              </Grid>
            )}

            {purchase.amountHomologated != null && (
              <Grid item xs={12} md={6}>
                <InfoField icon={<AttachMoneyIcon />} label="Valor Homologado">
                  <Typography variant="body1" fontWeight={600} color="success.main">
                    {formatCurrency(purchase.amountHomologated)}
                  </Typography>
                </InfoField>
              </Grid>
            )}

            {purchase.numberControlePncp && (
              <Grid item xs={12} md={6}>
                <InfoField icon={<VerifiedIcon />} label="Número de Controle PNCP">
                  <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>{purchase.numberControlePncp}</Typography>
                </InfoField>
              </Grid>
            )}

            {purchase.informationComplementary && (
              <Grid item xs={12}>
                <InfoField icon={<DescriptionIcon />} label="Informações Complementares">
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>{purchase.informationComplementary}</Typography>
                </InfoField>
              </Grid>
            )}
          </Grid>
        </Paper>

        {/* Datas */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <CalendarTodayIcon sx={{ mr: 1 }} />
            Cronograma
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Grid container spacing={2}>
            {[
              { label: 'Publicação no PNCP', value: formatDate(purchase.publishedPncpOn) },
              { label: 'Início das Propostas', value: formatDateTime(purchase.proposalOpeningDate) },
              { label: 'Fim das Propostas', value: formatDateTime(purchase.proposalCloseDate) },
            ].map((date) => (
              <Grid item xs={12} sm={4} key={date.label}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {date.label}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {date.value}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Paper>

        {/* Itens */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <ListAltIcon sx={{ mr: 1 }} />
            Itens
          </Typography>
          <Divider sx={{ mb: 2 }} />

          {items.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Nº</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Quantidade</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Valor Unitário</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Valor Total</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Situação</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item) => (
                    <TableRow key={item.number}>
                      <TableCell>{item.number}</TableCell>
                      <TableCell sx={{ minWidth: 220 }}>
                        <Typography variant="body2">{item.description || '-'}</Typography>
                        {item.typeItemDescription && (
                          <Typography variant="caption" color="text.secondary">{item.typeItemDescription}</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                        {item.quantity?.toLocaleString('pt-BR')} {item.unitMeasure}
                      </TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(item.unitValueEstimated)}</TableCell>
                      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{formatCurrency(item.amount)}</TableCell>
                      <TableCell>{item.situationDescription || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {loadingItems && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={32} />
            </Box>
          )}

          {itemsError && (
            <Alert
              severity="warning"
              sx={{ mt: 2 }}
              action={
                <Button color="inherit" size="small" onClick={() => loadItems(bidding.id, itemsPage + 1)}>
                  Tentar novamente
                </Button>
              }
            >
              {itemsError}
            </Alert>
          )}

          {!loadingItems && !itemsError && items.length === 0 && (
            <Alert severity="info">Nenhum item informado para esta licitação.</Alert>
          )}

          {hasMoreItems && !loadingItems && !itemsError && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button variant="outlined" onClick={() => loadItems(bidding.id, itemsPage + 1)} sx={{ textTransform: 'none' }}>
                Carregar mais itens
              </Button>
            </Box>
          )}
        </Paper>

        {/* Documentos */}
        {documents.length > 0 ? (
          <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <DescriptionIcon sx={{ mr: 1 }} />
              Documentos ({documents.length})
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <List>
              {documents.map((doc) => (
                <ListItem
                  key={doc.number}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    mb: 1,
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                  secondaryAction={
                    doc.fileUrl && (
                      <Tooltip title="Abrir documento no PNCP" arrow>
                        <IconButton
                          edge="end"
                          color="primary"
                          onClick={() => openExternalLink(doc.fileUrl)}
                          sx={{
                            '&:hover': {
                              backgroundColor: 'primary.main',
                              color: 'white',
                            },
                          }}
                        >
                          <OpenInNewIcon />
                        </IconButton>
                      </Tooltip>
                    )
                  }
                >
                  <ListItemIcon>
                    <DescriptionIcon color="error" sx={{ fontSize: 36 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={doc.title || doc.documentTypeDescription || 'Documento'}
                    secondary={
                      <Typography variant="caption" display="block" component="span">
                        {doc.documentTypeDescription}
                        {doc.publishDate && ` · Publicado em ${formatDate(doc.publishDate)}`}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        ) : (
          <Alert severity="info" sx={{ mt: 2 }}>
            Nenhum documento disponível para esta licitação.
          </Alert>
        )}

        {backButton}
      </Container>
    </PageContainer>
  );
}
